'use client'

import { useState } from 'react'
import { QueryClientProvider } from '@tanstack/react-query'
import { useMutationErrorQueryClient } from '@/components/providers/MutationErrorSurface'
import { httpBatchLink } from '@trpc/client'
import superjson from 'superjson'
import { trpc } from '@/lib/trpc/client'
import { getShopifySessionToken, clearShopifySessionTokenCache } from '@/lib/shopify-bridge'

function getBaseUrl() {
  if (typeof window !== 'undefined') return ''
  if (process.env.SHOPIFY_APP_URL) return process.env.SHOPIFY_APP_URL
  return `http://localhost:${process.env.PORT ?? 3000}`
}

interface TRPCProviderProps {
  children: React.ReactNode
}

export function TRPCProvider({ children }: TRPCProviderProps) {
  // The client and its global mutation-error toast are built together — see
  // MutationErrorSurface. Queries throw to the error boundary; MUTATIONS now
  // always say something to the merchant, instead of each page having to
  // remember to wire that up.
  const [queryClient, mutationErrorToast] = useMutationErrorQueryClient()

  const [trpcClient] = useState(() =>
    trpc.createClient({
      links: [
        httpBatchLink({
          url: `${getBaseUrl()}/api/trpc`,
          transformer: superjson,
          async headers() {
            const token = await getShopifySessionToken()
            return token ? { Authorization: `Bearer ${token}` } : {}
          },
          // Shopify session tokens are only valid for 60s. If one expires in
          // flight the server 401s; mint a fresh token and retry once rather
          // than surfacing a spurious auth error to the merchant.
          async fetch(url, options) {
            let res = await fetch(url, options)
            if (res.status === 401) {
              clearShopifySessionTokenCache()
              const token = await getShopifySessionToken()
              if (token) {
                const headers = new Headers(options?.headers as HeadersInit | undefined)
                headers.set('Authorization', `Bearer ${token}`)
                res = await fetch(url, { ...options, headers })
              }
            }
            return res
          },
        }),
      ],
    })
  )

  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>
        {children}
        {mutationErrorToast}
      </QueryClientProvider>
    </trpc.Provider>
  )
}
